import { useState, useCallback, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/Icon'
import { BaseFile } from '@/components/common/FileCard'
import { Plus, Search } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import type { ColumnDef } from '@tanstack/react-table'
import {
  datasetFilesService,
  TablePageRecordsResponse,
} from '@/api/services/datasetFiles.service'
import { useKnowledgeStore } from '@/stores/knowledgeStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@you/ui'

interface DatasetDetailTableProps {
  fileList: BaseFile[]
  onAddFile?: () => void
  onDeleteFile?: (fileId: string) => void
  onFileSelect?: (fileId: string) => void
}

const DEFAULT_PAGE_SIZE = 10

// 每页条数选项
const PAGE_SIZE_OPTIONS = [
  { value: '10', label: '10条/页' },
  { value: '20', label: '20条/页' },
  { value: '50', label: '50条/页' },
  { value: '100', label: '100条/页' },
]

const DatasetDetailTable: React.FC<DatasetDetailTableProps> = ({
  fileList,
  onAddFile,
  onDeleteFile,
  onFileSelect,
}) => {
  const { currentKnowledge, currentDataset } = useKnowledgeStore()
  const { currentWorkspace } = useWorkspaceStore()

  // 搜索关键词
  const [keyword, setKeyword] = useState('')
  // 当前选中的文档
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>(
    fileList[0]?.id || ''
  )
  // 表格数据
  const [tableData, setTableData] = useState<TablePageRecordsResponse>()
  // 加载状态
  const [loading, setLoading] = useState(false)
  // 加载失败（例如未解析，接口报错）
  const [loadFailed, setLoadFailed] = useState(false)
  // 当前页码
  const [currentPage, setCurrentPage] = useState(1)
  // 每页大小
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  // 过滤文件列表
  const filteredFileList = fileList.filter((file) =>
    file.name.toLowerCase().includes(keyword.toLowerCase())
  )

  // 获取当前选中的文档
  const selectedDocument = fileList.find((doc) => doc.id === selectedDocumentId)

  /**
   * 处理文档选择
   */
  const handleDocumentSelect = useCallback(
    (documentId: string) => {
      setSelectedDocumentId(documentId)
      onFileSelect?.(documentId)
    },
    [onFileSelect]
  )

  /**
   * 处理删除文件
   */
  const handleDeleteFile = useCallback(
    (fileId: string, event: React.MouseEvent) => {
      event.stopPropagation() // 阻止事件冒泡
      onDeleteFile?.(fileId)
    },
    [onDeleteFile]
  )

  /**
   * 渲染文档列表项
   */
  const renderDocumentItem = useCallback(
    (document: BaseFile) => {
      const isSelected = document.id === selectedDocumentId

      return (
        <div
          key={document.id}
          className={`
            h-12 px-2 rounded-lg flex items-center justify-between cursor-pointer transition-colors
            ${
              isSelected
                ? 'bg-[#F9FAFF] text-[#333333]'
                : 'text-[#333333] hover:bg-gray-50'
            }
          `}
          onClick={() => handleDocumentSelect(document.id)}
        >
          <span className="text-base truncate flex-1">{document.name}</span>
          <button
            onClick={(e) => handleDeleteFile(document.id, e)}
            className="flex items-center justify-center w-6 h-6 text-gray-500 hover:text-red-500 transition-colors"
            title="删除文件"
            aria-label="删除文件"
          >
            <Icon name="delete" size={20} />
          </button>
        </div>
      )
    },
    [selectedDocumentId, handleDocumentSelect, handleDeleteFile]
  )

  /**
   * 渲染搜索和添加区域
   */
  const renderSearchAndAdd = () => (
    <div className="flex items-center justify-between p-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          placeholder="搜索"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          className="px-0 pl-9 pr-2"
        />
      </div>
      <Button
        size="sm"
        onClick={onAddFile}
        className="w-[100px] h-8 flex-none gradient-primary ml-2"
      >
        <Plus size={14} />
        添加
      </Button>
    </div>
  )

  /**
   * 渲染文件列表
   */
  const renderFileList = () => (
    <div className="flex-1 px-4 overflow-y-auto">
      <div className="space-y-2 group">
        {filteredFileList.length > 0 ? (
          filteredFileList.map(renderDocumentItem)
        ) : (
          <div className="text-center text-gray-500 py-8">
            <div className="text-sm">
              {keyword ? '未找到匹配的表格' : '暂无表格'}
            </div>
          </div>
        )}
      </div>
    </div>
  )

  /**
   * 渲染左侧文档列表
   */
  const renderDocumentList = () => (
    <div className="flex flex-col h-full bg-white rounded-lg">
      {renderSearchAndAdd()}
      {renderFileList()}
    </div>
  )

  /**
   * 渲染右侧预览头部
   */
  const renderPreviewHeader = () => (
    <div className="flex items-center justify-between mb-2">
      {/* 左侧：当前文件名称 */}
      <div className="flex-1">
        <h3 className="text-lg font-bold text-[#655093] truncate">
          {selectedDocument?.name || '请选择表格'}
        </h3>
      </div>
    </div>
  )

  /**
   * 加载表格数据
   */
  const loadTableData = useCallback(async () => {
    if (
      !selectedDocumentId ||
      !currentDataset?.id ||
      !currentKnowledge?.id ||
      !currentWorkspace?.id
    ) {
      return
    }

    setLoadFailed(false)
    setLoading(true)
    try {
      const response = await datasetFilesService.getTablePageRecords({
        datasetFileId: parseInt(selectedDocumentId),
        datasetId: parseInt(currentDataset.id),
        knowledgeBaseId: parseInt(currentKnowledge.id),
        workspaceId: parseInt(currentWorkspace.id),
        pageNo: currentPage,
        pageSize: pageSize,
      })
      console.log('response', response)
      setTableData(response)
    } catch (error) {
      console.error('加载表格数据失败:', error)
      setLoadFailed(true)
      setTableData(undefined)
    } finally {
      setLoading(false)
    }
  }, [
    selectedDocumentId,
    currentDataset?.id,
    currentKnowledge?.id,
    currentWorkspace?.id,
    currentPage,
    pageSize,
  ])

  // 监听选中文档变化，加载表格数据
  useEffect(() => {
    loadTableData()
  }, [loadTableData])

  /**
   * 处理页码变化
   */
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  /**
   * 处理每页大小变化
   */
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1) // 重置到第一页
  }, [])

  /**
   * 渲染分页组件
   */
  const renderPagination = () => {
    console.log('tableData', tableData)
    // if (
    //   !tableData ||
    //   !tableData.total ||
    //   parseInt(tableData.total) <= pageSize
    // ) {
    //   return null
    // }

    const totalPages = Math.ceil(parseInt(tableData?.total || '0') / pageSize)
    const currentPageNum = currentPage

    // 生成页码数组
    const getPageNumbers = () => {
      const pages: Array<number | 'ellipsis'> = []
      const maxVisiblePages = 5

      if (totalPages <= maxVisiblePages) {
        // 如果总页数小于等于最大可见页数，显示所有页码
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i)
        }
      } else {
        // 否则显示部分页码和省略号
        if (currentPageNum <= 3) {
          // 当前页在前3页
          for (let i = 1; i <= 4; i++) {
            pages.push(i)
          }
          pages.push('ellipsis')
          pages.push(totalPages)
        } else if (currentPageNum >= totalPages - 2) {
          // 当前页在后3页
          pages.push(1)
          pages.push('ellipsis')
          for (let i = totalPages - 3; i <= totalPages; i++) {
            pages.push(i)
          }
        } else {
          // 当前页在中间
          pages.push(1)
          pages.push('ellipsis')
          for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
            pages.push(i)
          }
          pages.push('ellipsis')
          pages.push(totalPages)
        }
      }

      return pages
    }

    const pageNumbers = getPageNumbers()

    return (
      <div className="flex items-center justify-end mt-4 space-x-4 pr-1">
        <div className="text-sm text-gray-600">
          共 {tableData?.total || '0'} 条
        </div>
        <div className="flex items-center space-x-4">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (currentPageNum > 1) {
                      handlePageChange(currentPageNum - 1)
                    }
                  }}
                  className={
                    currentPageNum <= 1 ? 'pointer-events-none opacity-50' : ''
                  }
                  size="default"
                />
              </PaginationItem>

              {pageNumbers.map((page, index) => (
                <PaginationItem key={index}>
                  {page === 'ellipsis' ? (
                    <PaginationEllipsis />
                  ) : (
                    <PaginationLink
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        handlePageChange(page)
                      }}
                      isActive={page === currentPageNum}
                      size="icon"
                    >
                      {page}
                    </PaginationLink>
                  )}
                </PaginationItem>
              ))}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (currentPageNum < totalPages) {
                      handlePageChange(currentPageNum + 1)
                    }
                  }}
                  className={
                    currentPageNum >= totalPages
                      ? 'pointer-events-none opacity-50'
                      : ''
                  }
                  size="default"
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>

          {/* 每页条数选择 */}
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => handlePageSizeChange(parseInt(value))}
          >
            <SelectTrigger className="w-[100px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    )
  }

  /**
   * 渲染数据结构预览内容
   */
  const renderStructureContent = () => {
    if (!selectedDocument) {
      return (
        <div className="text-center text-gray-500 py-8">
          <div className="text-lg mb-2">🏗️</div>
          <div>请先选择表格</div>
        </div>
      )
    }

    if (loadFailed) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center text-gray-600">
            <div className="text-2xl mb-3">🛠️</div>
            <div className="mb-2">该表格未完成解析流程，暂无法预览数据</div>
            <Button onClick={onAddFile} className="bg-gradient-primary mt-2">
              前往导入流程
            </Button>
          </div>
        </div>
      )
    }

    if (!tableData?.records?.length) {
      return (
        <div className="text-center text-gray-500 py-8">
          <div className="text-lg mb-2">📊</div>
          <div>暂无数据</div>
        </div>
      )
    }

    // 使用tableColumns来定义列
    const columns: ColumnDef<any, any>[] = (tableData.tableColumns || [])
      .sort((a, b) => a.columnNo - b.columnNo) // 按columnNo排序
      .map((column) => ({
        accessorKey: column.columnName,
        header: column.columnName,
        cell: ({ row }) => {
          const value = row.getValue(column.columnName)
          // 根据列类型格式化显示
          switch (column.columnType) {
            case 'Number':
              return typeof value === 'number' ? value.toLocaleString() : value
            default:
              return value
          }
        },
      }))

    return (
      <div className="relative overflow-auto">
        <DataTable
          columns={columns}
          data={tableData.records}
          stickyHeader
          maxHeight={500}
          stripe
          enablePagination={true}
          pagination={{
            pageIndex: currentPage - 1, // tanstack table 使用 0-based 索引
            pageSize: pageSize,
          }}
          onPaginationChange={(pagination) => {
            setCurrentPage(pagination.pageIndex + 1) // 转换为 1-based 索引
            setPageSize(pagination.pageSize)
          }}
        />
        {renderPagination()}

        {/* 加载遮罩层 */}
        {loading && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span>加载中...</span>
            </div>
          </div>
        )}
      </div>
    )
  }

  /**
   * 渲染右侧预览区域
   */
  const renderPreviewArea = () => (
    <div className="flex flex-col h-full">
      {/* 头部：文件名称 */}
      {renderPreviewHeader()}

      {/* 内容区域 */}
      <div className="flex-1 bg-white rounded-lg p-4 overflow-y-auto">
        {renderStructureContent()}
      </div>
    </div>
  )

  return (
    <div className="h-full min-w-[1200px] p-2">
      {/* 两栏布局 */}
      <div className="flex gap-2 h-full">
        {/* 左侧：表格列表 */}
        <div className="w-[350px] flex-none">{renderDocumentList()}</div>

        {/* 右侧：预览区域 */}
        <div className="flex-1">{renderPreviewArea()}</div>
      </div>
    </div>
  )
}

export default DatasetDetailTable
